/**
 * Access Control Types
 *
 * Defines typed access control structures for document permissions.
 * Implements role-based access control with clear hierarchy.
 *
 * @module types/acl
 * @see SPEC-015 Requirements 1.1, 1.4, 1.5, 1.6, 2.1, 2.2, 2.3, 2.4
 */
/**
 * Document access roles with clear hierarchy.
 * OWNER > EDITOR > VIEWER
 *
 * @see Requirement 1.1
 */
export var DocRole;
(function (DocRole) {
    DocRole["OWNER"] = "OWNER";
    DocRole["EDITOR"] = "EDITOR";
    DocRole["VIEWER"] = "VIEWER";
})(DocRole || (DocRole = {}));
/**
 * Role hierarchy mapping for permission checks.
 * Higher numeric values indicate more permissions.
 *
 * @see Requirement 2.4
 */
export const ROLE_HIERARCHY = {
    [DocRole.OWNER]: 3,
    [DocRole.EDITOR]: 2,
    [DocRole.VIEWER]: 1,
};
/**
 * Checks if a user's role has sufficient permissions for a required role.
 *
 * @param userRole - The role assigned to the user
 * @param requiredRole - The minimum role required for the action
 * @returns true if userRole has equal or higher permissions than requiredRole
 *
 * @example
 * hasPermission(DocRole.OWNER, DocRole.EDITOR); // true
 * hasPermission(DocRole.VIEWER, DocRole.EDITOR); // false
 *
 * @see Requirement 2.4
 */
export function hasPermission(userRole, requiredRole) {
    return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}
/**
 * Checks if the given role can read the document.
 * Any valid role (OWNER, EDITOR, VIEWER) can read.
 *
 * @param role - The role to check, or undefined if user has no access
 * @returns true if the role allows reading
 *
 * @example
 * canRead(DocRole.VIEWER); // true
 * canRead(undefined); // false
 *
 * @see Requirement 2.1
 */
export function canRead(role) {
    return role !== undefined;
}
/**
 * Checks if the given role can edit the document.
 * Only OWNER and EDITOR roles can edit.
 *
 * @param role - The role to check, or undefined if user has no access
 * @returns true if the role allows editing
 *
 * @example
 * canEdit(DocRole.EDITOR); // true
 * canEdit(DocRole.VIEWER); // false
 *
 * @see Requirement 2.2
 */
export function canEdit(role) {
    return role === DocRole.OWNER || role === DocRole.EDITOR;
}
/**
 * Checks if the given role is the document owner.
 * Only OWNER role returns true.
 *
 * @param role - The role to check, or undefined if user has no access
 * @returns true if the role is OWNER
 *
 * @example
 * isOwner(DocRole.OWNER); // true
 * isOwner(DocRole.EDITOR); // false
 *
 * @see Requirement 2.3
 */
export function isOwner(role) {
    return role === DocRole.OWNER;
}
//# sourceMappingURL=acl.js.map